import { useCallback, useEffect, useRef, useState } from 'react'
import { parseHostname, type LogoBackdrop } from '@mondaygpt/types'
import {
  streamWebsiteScan,
  type ScanStreamEvent,
  type ScanScreenshotPayload,
  type ScanInstagramProfilePayload,
  type WebsiteScanResult,
} from 'modules/creative/api/brand-kit-api'

/** Re-export so existing import sites (`./use-scan-stream`) keep working. */
export { parseHostname as parseWebsiteHost }

/**
 * Coarse-grained, event-driven phases. Discovery rows in ScanningStep reveal
 * off the data itself, not the phase, so phases only need to gate the polaroid
 * lifecycle and the parent's advance.
 */
export type ScanPhase = 'idle' | 'visiting' | 'studying' | 'complete' | 'error'

export interface ScrapeInput {
  websiteUrl?: string
  instagramHandle?: string
  facebookHandle?: string
}

export type ScrapeKind = 'website' | 'social' | 'name-only'

export function pickScrapeKind(input: ScrapeInput): ScrapeKind {
  if (input.instagramHandle || input.facebookHandle) return 'social'
  if (input.websiteUrl) return 'website'
  return 'name-only'
}

export interface ScanStatus {
  phase: ScanPhase
  /** Flips true after FINAL_HOLD_MS once the scan reaches `complete`. Parent uses this to advance to confirm step. */
  isFinal: boolean
  profile?: Partial<WebsiteScanResult>
  screenshot?: ScanScreenshotPayload
  /** Real Instagram profile data from Business Discovery — drives the IG polaroid mockup. */
  instagramProfile?: ScanInstagramProfilePayload
  logoUrl?: string | null
  /** Which backdrop the logo chip renders on. Undefined → treat as 'light'. */
  logoBackdrop?: LogoBackdrop
  error?: string
}

/** Lingers in `complete` so the polaroid can play its exit before the parent advances. */
const FINAL_HOLD_MS = 1500

const INITIAL_STATUS: ScanStatus = { phase: 'idle', isFinal: false }

/** UI-relevant fields. Used to short-circuit profile-partial setStates that don't change anything visible. */
const PARTIAL_FIELDS: ReadonlyArray<keyof WebsiteScanResult> = [
  'businessName',
  'industry',
  'description',
  'targetAudience',
  'colors',
  'suggestedTones',
  'sections',
  'logoUrl',
]

/**
 * Compares only the fields the UI reads. Object/array fields (`colors`,
 * `suggestedTones`, `sections`) get a structural compare via JSON because
 * each LLM partial materializes fresh references — without this the check
 * would return true on every partial that includes any of those fields.
 */
function partialChanges(
  prev: Partial<WebsiteScanResult> | undefined,
  next: Partial<WebsiteScanResult>
): boolean {
  for (const k of PARTIAL_FIELDS) {
    if (!(k in next)) continue
    const a = prev?.[k]
    const b = next[k]
    if (a === b) continue
    if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
      if (JSON.stringify(a) === JSON.stringify(b)) continue
    }
    return true
  }
  return false
}

const bumpToStudying = (phase: ScanPhase): ScanPhase => (phase === 'visiting' ? 'studying' : phase)

export function useScanStream() {
  const [status, setStatus] = useState<ScanStatus>(INITIAL_STATUS)
  const abortRef = useRef<(() => void) | null>(null)
  const finalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Per-run token. Incremented on every start() call. All async work captures
  // its own runId on entry and bails (without mutating state) when a newer
  // run has begun. Without this, events from a prior in-flight generator
  // splice into the new run's state on rapid re-submit.
  const runIdRef = useRef(0)

  const clearFinalTimer = useCallback(() => {
    if (finalTimerRef.current) {
      clearTimeout(finalTimerRef.current)
      finalTimerRef.current = null
    }
  }, [])

  const start = useCallback(
    async (input: ScrapeInput) => {
      const myRunId = ++runIdRef.current
      abortRef.current?.()
      clearFinalTimer()
      setStatus({ phase: 'visiting', isFinal: false })

      const { events, abort } = streamWebsiteScan(input)
      abortRef.current = abort

      const isStale = () => myRunId !== runIdRef.current

      try {
        for await (const event of events) {
          if (isStale()) return
          applyEvent(event)
        }
      } catch (err) {
        if (isStale()) return
        setStatus(curr => ({
          ...curr,
          phase: 'error',
          error: err instanceof Error ? err.message : 'scan_failed',
        }))
      } finally {
        // No `return` inside the finally — eslint `no-unsafe-finally` would
        // complain (it overrides try/catch return values). Invert the guard
        // and only schedule the settle when the run is still current.
        if (!isStale()) {
          finalTimerRef.current = setTimeout(() => {
            if (isStale()) return
            setStatus(curr => ({
              ...curr,
              // Preserve a terminal error phase — only flip to `complete` when
              // the stream actually succeeded.
              phase: curr.phase === 'error' ? 'error' : 'complete',
              isFinal: true,
            }))
          }, FINAL_HOLD_MS)
        }
      }

      function applyEvent(event: ScanStreamEvent) {
        switch (event.type) {
          case 'started':
            return
          case 'screenshot':
            setStatus(curr => ({
              ...curr,
              phase: bumpToStudying(curr.phase),
              screenshot: { url: event.url },
            }))
            return
          case 'screenshot-failed':
            setStatus(curr => ({ ...curr, phase: bumpToStudying(curr.phase) }))
            return
          case 'instagram-profile': {
            // Drop the discriminator before storing — `ScanStatus.instagramProfile`
            // is the payload shape, not the event shape.
            const { type: _t, ...payload } = event
            setStatus(curr => ({
              ...curr,
              phase: bumpToStudying(curr.phase),
              instagramProfile: payload,
            }))
            return
          }
          case 'logo':
            // Early logo arrival — lets the DiscoveryLogoImg swap from the
            // favicon placeholder to the real logo well before profile-final.
            // profile-final later overwrites this with the canonical priority
            // (scrapedLogo ?? socialLogo ?? favicon).
            setStatus(curr => ({
              ...curr,
              logoUrl: event.logoUrl,
              logoBackdrop: event.logoBackdrop ?? curr.logoBackdrop,
            }))
            return
          case 'profile-partial':
            setStatus(curr => {
              const nextPhase = bumpToStudying(curr.phase)
              if (curr.phase === nextPhase && !partialChanges(curr.profile, event.profile)) {
                return curr
              }
              return {
                ...curr,
                phase: nextPhase,
                profile: { ...curr.profile, ...event.profile },
              }
            })
            return
          case 'profile-final':
            setStatus(curr => ({
              ...curr,
              phase: 'complete',
              profile: event.profile,
              logoUrl: event.logoUrl,
              logoBackdrop: event.logoBackdrop ?? event.profile.logoBackdrop ?? curr.logoBackdrop,
            }))
            return
          case 'error':
            setStatus(curr => ({ ...curr, phase: 'error', error: event.error }))
            return
          case 'done':
            return
        }
      }
    },
    [clearFinalTimer]
  )

  useEffect(
    () => () => {
      // Bump the runId so any in-flight generator's next event is treated as stale.
      runIdRef.current++
      abortRef.current?.()
      clearFinalTimer()
    },
    [clearFinalTimer]
  )

  return { status, start }
}
